/**
 * Session state: the MSAL account, and the identity the backend confirms.
 *
 * Two things on purpose. The account is what the browser holds; `GET /me` is what the
 * backend says after validating the token — including `isAdmin`, which the frontend must
 * never infer from a claim it read itself.
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { getMe, setTokenProvider } from '@/api/client.js'
import { createAuthProvider, isProviderConfigured } from '@/auth/provider.js'
import { config } from '@/config.js'

export const useAuthStore = defineStore('auth', () => {
  /** @type {import('vue').Ref<'idle'|'loading'|'authenticated'|'anonymous'|'error'>} */
  const status = ref('idle')
  const account = ref(null)
  /** @type {import('vue').Ref<import('@/api/client.js').Identity|null>} */
  const identity = ref(null)
  const error = ref(null)

  let provider = null

  const isAuthenticated = computed(() => status.value === 'authenticated')
  const isAdmin = computed(() => identity.value?.isAdmin === true)

  /** False when the Entra registration has not been wired up on this instance. */
  const providerConfigured = computed(() => isProviderConfigured(config))
  /**
   * The value the backend writes on a preview's owner label, taken from `/me` and not
   * derived from the upn: guessing it would make "My previews" quietly match nothing.
   * MSAL's account is only a fallback for the moment before `/me` answers.
   */
  const ownerLabel = computed(
    () => identity.value?.ownerLabel || account.value?.username || '',
  )
  const displayName = computed(
    () => identity.value?.name || account.value?.name || ownerLabel.value,
  )

  /**
   * Boot the session. Called once from main.js before the app mounts, so the router
   * never has to guess whether authentication has resolved.
   */
  async function initialize() {
    if (status.value === 'loading') return
    status.value = 'loading'
    error.value = null
    try {
      provider = createAuthProvider(config)
      setTokenProvider(() => (provider ? provider.getToken() : Promise.resolve(null)))
      await provider.initialize()
      account.value = provider.getAccount()
      if (!account.value) {
        status.value = 'anonymous'
        return
      }
      identity.value = await getMe()
      status.value = 'authenticated'
    } catch (cause) {
      error.value = cause
      status.value = 'error'
    }
  }

  /**
   * Start the redirect flow. It normally navigates away and never resolves; only the
   * stubbed provider returns. Failures go on screen rather than to the console: MSAL
   * refuses at this click when the registration is wrong, and "nothing happens when I
   * click" is the worst possible symptom.
   */
  async function signIn() {
    error.value = null
    try {
      if (!provider) provider = createAuthProvider(config)
      await provider.signIn()
      account.value = provider.getAccount()
      if (account.value) {
        identity.value = await getMe()
        status.value = 'authenticated'
      }
    } catch (cause) {
      error.value = cause
      status.value = 'error'
    }
  }

  async function signOut() {
    if (!provider) return
    await provider.signOut()
    account.value = null
    identity.value = null
    status.value = 'anonymous'
  }

  /**
   * Whether the screen offers the controls that change this preview. The wire field
   * decides; the local rule is the fallback for a payload carrying none.
   */
  function canModify(preview) {
    if (!preview) return false
    if (typeof preview.canModify === 'boolean') return preview.canModify
    return isAdmin.value || preview.owner === ownerLabel.value
  }

  return {
    status,
    account,
    identity,
    error,
    isAuthenticated,
    isAdmin,
    ownerLabel,
    displayName,
    providerConfigured,
    initialize,
    signIn,
    signOut,
    canModify,
  }
})
